import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type AuditEvent,
  type Translate,
  useWorkspaceAudit,
  useWorkspaceMembers,
} from '@/entities/collab';
import { useViewer } from '@/entities/viewer';
import type { Workspace } from '@/entities/workspace';
import { Avatar, Button, ErrorState, Icons, LoadingState } from '@/shared/ui';

import styles from './Admin.module.css';

type Glyph = () => React.JSX.Element;

/** The icon of an event by its family (the part before the first dot). */
function glyphOf(eventType: string): Glyph {
  const family = eventType.split('.')[0] ?? '';
  switch (family) {
    case 'workspace':
      return Icons.workspace;
    case 'member':
      return Icons.users;
    case 'team':
      return Icons.layers;
    case 'role':
      return Icons.shield;
    case 'board_label':
    case 'board':
      return eventType.startsWith('board.owner') ? Icons.crown : Icons.board;
    case 'access_rule':
      return Icons.key;
    case 'invitation':
    case 'invite_link':
      return Icons.mail;
    case 'request':
    case 'transfer':
      return Icons.inbox;
    default:
      return Icons.history;
  }
}

function dayKey(iso: string, zone: string | undefined): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      dateStyle: 'short',
      ...(zone && { timeZone: zone }),
    }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}

/**
 * Read-only trail of administrative events: grouped by day, newest first, with an icon per kind,
 * the actor prominent, and the affected object named. Everything shown comes from the audit
 * record itself (nothing is invented); deleted people keep the name they had at the time.
 */
export function AuditTab({ workspace }: { workspace: Workspace }) {
  const { t, i18n } = useTranslation();
  const tr = t as unknown as Translate;
  const viewer = useViewer();
  const zone = viewer.status === 'authenticated' ? viewer.user.timezone : undefined;
  const events = useWorkspaceAudit(workspace.id);
  const members = useWorkspaceMembers(workspace.id);
  const names = useMemo(
    () => new Map((members.data ?? []).map((m) => [m.user_id, m.display_name])),
    [members.data],
  );
  const all: AuditEvent[] = useMemo(() => events.data?.pages.flat() ?? [], [events.data]);
  const groups = useMemo(() => {
    const result: { day: string; label: string; items: AuditEvent[] }[] = [];
    for (const event of all) {
      const key = dayKey(event.created_at, zone);
      let group = result.find((g) => g.day === key);
      if (!group) {
        let label = key;
        try {
          label = new Intl.DateTimeFormat(i18n.language, {
            dateStyle: 'full',
            ...(zone && { timeZone: zone }),
          }).format(new Date(event.created_at));
        } catch {
          /* keep the numeric day */
        }
        group = { day: key, label, items: [] };
        result.push(group);
      }
      group.items.push(event);
    }
    return result;
  }, [all, i18n.language, zone]);

  if (events.isPending) return <LoadingState />;
  if (events.isError) return <ErrorState error={events.error} />;
  if (all.length === 0) return <p className={styles.note}>{t('workspaceAdmin.auditEmpty')}</p>;

  const time = (iso: string) => {
    try {
      return new Intl.DateTimeFormat(i18n.language, {
        timeStyle: 'short',
        ...(zone && { timeZone: zone }),
      }).format(new Date(iso));
    } catch {
      return iso.slice(11, 16);
    }
  };
  return (
    <div className={styles.panel}>
      {groups.map((group) => (
        <section key={group.day} aria-label={group.label} className={styles.auditDay}>
          <h3 className={styles.auditDayHeading}>{group.label}</h3>
          <ul className={styles.list} aria-label={t('workspaceAdmin.tabs.audit')}>
            {group.items.map((event) => {
              const key = `audit.events.${event.event_type.replace(/\./g, '_')}`;
              const text = tr(key);
              const Glyph = glyphOf(event.event_type);
              const object =
                (typeof event.payload['name'] === 'string' && event.payload['name']) ||
                (typeof event.payload['board_name'] === 'string' && event.payload['board_name']) ||
                null;
              const subject = event.subject_user_id
                ? (names.get(event.subject_user_id) ?? null)
                : null;
              const actor = event.actor_name ?? t('workspaceAdmin.auditUnknownActor');
              return (
                <li key={event.id} className={styles.auditRow} data-testid="audit-event">
                  <span className={styles.auditIcon}>
                    <Glyph />
                  </span>
                  <span className={styles.grow}>
                    <span className={styles.auditLine}>
                      {event.actor_user_id && (
                        <Avatar id={event.actor_user_id} name={actor} size="sm" />
                      )}
                      <strong className={styles.auditActor}>{actor}</strong>
                      <span>{text === key ? event.event_type : text}</span>
                    </span>
                    {(object || subject) && (
                      <span className={styles.auditObjects}>
                        {object && <span className={styles.chip}>{object}</span>}
                        {subject && <span className={styles.chip}>{subject}</span>}
                      </span>
                    )}
                  </span>
                  <time className={styles.meta} dateTime={event.created_at}>
                    {time(event.created_at)}
                  </time>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
      {events.hasNextPage && (
        <div className={styles.actions}>
          <Button
            disabled={events.isFetchingNextPage}
            onClick={() => {
              void events.fetchNextPage();
            }}
          >
            {t('workspaceAdmin.auditMore')}
          </Button>
        </div>
      )}
    </div>
  );
}
